import { useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import BulkImportTable, { type BulkRowDraft } from "@/components/b2b/BulkImportTable";
import { parseBulkPaste } from "@/lib/bulkPasteParser";
import { track } from "@/lib/track";
import { normalizePhoneForDb } from "@/lib/bulkOrderValidation";
import { suggestServiceIdFromNotes } from "@/lib/bulkServiceSuggestion";
import { formatCurrency } from "@/lib/currency";
import InvoiceBillingForm, { validateBillingInfo, type BillingInfo } from "@/components/booking/InvoiceBillingForm";

type VendorChoice = { id: string; company_name: string; slug?: string | null; city?: string | null; logo_url?: string | null };
type ServiceChoice = {
  id: string;
  name: string;
  description?: string | null;
  base_price?: number | null;
  vendor_covers_customer_fee?: boolean | null;
  display_fee_disclaimer?: boolean | null;
};

type WizardStep = 0 | 1 | 2 | 3;

const SERVICE_PRESETS = [
  { id: "pulizie", label: "Pulizie" },
  { id: "pulizie cambio", label: "Pulizie cambio" },
  { id: "pulizie fondo", label: "Pulizie fondo" },
  { id: "pulizie uffici", label: "Pulizie uffici" },
];

export default function BulkImportWizard({
  initialCompanyId,
  initialServiceId,
}: {
  initialCompanyId?: string;
  initialServiceId?: string;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState<WizardStep>(0);

  // Step 0: vendor/service selection
  const [favoriteVendors, setFavoriteVendors] = useState<VendorChoice[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorChoice | null>(null);
  const [services, setServices] = useState<ServiceChoice[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");

  const [searchCity, setSearchCity] = useState<string>("");
  const [searchService, setSearchService] = useState<string>("pulizie");
  const [searchResults, setSearchResults] = useState<VendorChoice[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Step 1/2: paste + edit
  const [rawPaste, setRawPaste] = useState<string>("");
  const [defaults, setDefaults] = useState<{ checkoutTime: string; checkinTime: string; bufferMinutes: number }>({
    checkoutTime: "11:00",
    checkinTime: "15:00",
    bufferMinutes: 60,
  });
  const [draftRows, setDraftRows] = useState<BulkRowDraft[]>([]);

  // Step 3: submit
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ batch_id: string; booking_ids: string[] } | null>(null);
  const [submittedSnapshot, setSubmittedSnapshot] = useState<{
    vendorName: string;
    rows: BulkRowDraft[];
  } | null>(null);
  const [profilePhone, setProfilePhone] = useState<string>("");
  const [customerPhone, setCustomerPhone] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<"card" | "cash" | "invoice">("card");
  const [requestInvoice, setRequestInvoice] = useState(false);
  const billingForm = useForm<{ billingType: "private" | "company"; billingInfo: BillingInfo | null; saveBillingToProfile: boolean }>({
    defaultValues: { billingType: "private", billingInfo: null, saveBillingToProfile: false },
  });

  const canContinueFromStep0 = !!selectedVendor?.id && !!selectedServiceId;
  const maxRows = 30;

  // Load customer phone from profile (if available)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const session = sess?.session;
        if (!session?.user) return;
        const userId = session.user.id;
        const { data: prof } = await supabase.from("profiles").select("phone").eq("user_id", userId).maybeSingle();
        const phone = String((prof as any)?.phone || "").trim();
        if (!active) return;
        setProfilePhone(phone);
        if (!customerPhone) setCustomerPhone(phone);
      } catch {
        // no-op
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved billing info when invoice or request invoice
  useEffect(() => {
    if (paymentMethod !== "invoice" && !requestInvoice) return;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        if (!sess?.session?.user?.id) return;
        const { data: prof } = await supabase.from("profiles").select("billing_info").eq("user_id", sess.session.user.id).maybeSingle();
        const saved = (prof as any)?.billing_info as BillingInfo | null | undefined;
        if (saved?.type && !billingForm.getValues("billingInfo")) {
          billingForm.setValue("billingType", saved.type as "private" | "company");
          billingForm.setValue("billingInfo", saved);
        }
      } catch {
        // no-op
      }
    })();
  }, [paymentMethod, requestInvoice]);

  // Load favorite vendors (recent bookings + favourites)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const session = sess?.session;
        if (!session?.user) return;

        const userId = session.user.id;
        const { data: prof } = await supabase.from("profiles").select("id").eq("user_id", userId).maybeSingle();
        const profileId = (prof as any)?.id as string | undefined;
        if (!profileId) return;

        const sb: any = supabase;
        const [fav, recent] = await Promise.all([
          sb
            .from("favourites")
            .select("companies!inner(id, company_name, slug, city, logo_url)")
            .eq("user_id", userId),
          sb
            .from("bookings")
            .select("company_id, companies!inner(id, company_name, slug, city, logo_url)")
            .eq("customer_id", profileId)
            .order("created_at", { ascending: false })
            .limit(50),
        ]);

        const out: VendorChoice[] = [];
        const seen = new Set<string>();
        for (const row of (fav?.data || []) as any[]) {
          const c = row?.companies;
          if (c?.id && !seen.has(c.id)) {
            seen.add(c.id);
            out.push({ id: c.id, company_name: c.company_name, slug: c.slug, city: c.city, logo_url: c.logo_url });
          }
        }
        for (const row of (recent?.data || []) as any[]) {
          const c = row?.companies;
          if (c?.id && !seen.has(c.id)) {
            seen.add(c.id);
            out.push({ id: c.id, company_name: c.company_name, slug: c.slug, city: c.city, logo_url: c.logo_url });
          }
        }

        if (!active) return;
        setFavoriteVendors(out);
      } catch {
        // non-blocking
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // If user arrived from a vendor/service entrypoint, preselect and skip Step 0.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!initialCompanyId) return;
        const sb: any = supabase;
        const { data, error } = await sb
          .from("companies")
          .select("id, company_name, slug, city, logo_url")
          .eq("id", initialCompanyId)
          .maybeSingle();
        if (error) throw error;
        if (!data?.id) return;
        if (!active) return;
        setSelectedVendor({
          id: data.id,
          company_name: data.company_name,
          slug: data.slug || null,
          city: data.city || null,
          logo_url: data.logo_url || null,
        });
        if (initialServiceId) setSelectedServiceId(initialServiceId);
        setStep(1);
      } catch {
        // non-blocking
      }
    })();
    return () => {
      active = false;
    };
  }, [initialCompanyId, initialServiceId]);

  // Load services when vendor changes
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setServices([]);
        setSelectedServiceId("");
        if (!selectedVendor?.id) return;
        const sb: any = supabase;
        const { data, error } = await sb
          .from("services")
          .select("id, name, description, base_price, is_public, is_active, vendor_covers_customer_fee, display_fee_disclaimer")
          .eq("company_id", selectedVendor.id)
          .eq("is_active", true);
        if (error) throw error;
        const svc = (data || [])
          .filter((s: any) => s?.is_public === true)
          .map((s: any) => ({
            id: s.id,
            name: s.name,
            description: s.description,
            base_price: s.base_price,
            vendor_covers_customer_fee: s.vendor_covers_customer_fee,
            display_fee_disclaimer: s.display_fee_disclaimer,
          }));
        if (!active) return;
        setServices(svc);
        if (svc.length === 1 && !initialServiceId) setSelectedServiceId(svc[0].id);
      } catch (e: any) {
        toast({ title: "Errore", description: e?.message || "Impossibile caricare i servizi del fornitore.", variant: "destructive" });
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedVendor?.id, toast, initialServiceId]);

  const stepLabel = useMemo(() => {
    switch (step) {
      case 0:
        return "Step 0: Seleziona fornitore e servizio";
      case 1:
        return "Step 1: Incolla lista (max 30 righe)";
      case 2:
        return "Step 2: Controlla e correggi";
      case 3:
        return "Step 3: Riepilogo e invio";
      default:
        return "";
    }
  }, [step]);

  const parsedSummary = useMemo(() => {
    const total = draftRows.length;
    const invalid = draftRows.filter((r) => (r.errors?.length || 0) > 0).length;
    return { total, invalid, valid: total - invalid };
  }, [draftRows]);

  const serviceCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of draftRows) {
      const sid = (r as any)?.serviceId || selectedServiceId;
      if (!sid) continue;
      map[sid] = (map[sid] || 0) + 1;
    }
    return map;
  }, [draftRows, selectedServiceId]);

  const pricingSummary = useMemo(() => {
    // Derive estimates from services.base_price (same value used by DB in latest migration).
    const byService: Array<{ serviceId: string; serviceName: string; count: number; unit: number | null; subtotal: number | null }> = [];
    let total = 0;
    let hasUnknown = false;
    for (const [sid, count] of Object.entries(serviceCounts)) {
      const svc = services.find((s) => s.id === sid);
      const unit = typeof svc?.base_price === "number" ? Number(svc.base_price) : null;
      const subtotal = unit !== null ? unit * count : null;
      if (subtotal !== null) total += subtotal;
      else hasUnknown = true;
      byService.push({ serviceId: sid, serviceName: svc?.name || "Servizio", count, unit, subtotal });
    }
    byService.sort((a, b) => (b.subtotal ?? -1) - (a.subtotal ?? -1));
    return { byService, total, hasUnknown };
  }, [serviceCounts, services]);

  /** Allineato a create_bulk_bookings_from_draft: min(2% del prezzo riga, 10€) per intervento se il vendor non copre la fee. */
  const customerFeeEstimate = useMemo(() => {
    let cents = 0;
    let anyFeeDisclaimer = false;
    for (const r of draftRows) {
      const sid = (r as { serviceId?: string }).serviceId || selectedServiceId;
      const svc = services.find((s) => s.id === sid);
      if (svc?.display_fee_disclaimer) anyFeeDisclaimer = true;
      const unit = typeof svc?.base_price === "number" ? Number(svc.base_price) : null;
      if (unit === null) continue;
      const rowCents = Math.round(unit * 100);
      if (svc?.vendor_covers_customer_fee === true) continue;
      cents += Math.min(Math.round(rowCents * 0.02), 1000);
    }
    return { cents, euros: cents / 100, anyFeeDisclaimer };
  }, [draftRows, selectedServiceId, services]);

  const doSearchVendors = async () => {
    setSearchLoading(true);
    try {
      const sb: any = supabase;
      const city = searchCity.trim();
      const term = searchService.trim();
      let vendors: VendorChoice[] = [];

      // Prefer RPC if available
      try {
        const { data: rpcData, error } = await sb.rpc("search_companies_by_service_areas", {
          search_city: city || null,
          search_term: term || null,
          service_categories: null,
          limit_count: 30,
        });
        if (!error && Array.isArray(rpcData)) {
          vendors = rpcData.map((c: any) => ({
            id: c.id,
            company_name: c.company_name,
            slug: c.slug || null,
            city: c.city,
            logo_url: c.logo_url,
          }));
        }
      } catch {
        // fall back below
      }

      if (vendors.length === 0) {
        let query = sb.from("companies").select("id, company_name, city, logo_url, slug").eq("is_active", true).eq("onboarding_completed", true);
        if (city) query = query.or(`city.ilike.%${city}%`);
        const { data } = await query.limit(30);
        vendors = (data || []).map((c: any) => ({
          id: c.id,
          company_name: c.company_name,
          slug: c.slug || null,
          city: c.city,
          logo_url: c.logo_url,
        }));
      }

      setSearchResults(vendors);
      if (vendors.length === 0) {
        toast({ title: "Nessun fornitore trovato", description: "Prova a cambiare città o servizio.", variant: "default" });
      }
    } catch (e: any) {
      toast({ title: "Errore", description: e?.message || "Ricerca fornitori fallita.", variant: "destructive" });
    } finally {
      setSearchLoading(false);
    }
  };

  const parseNow = () => {
    if (!rawPaste.trim()) {
      toast({ title: "Incolla la lista", description: "Incolla le righe da email/WhatsApp/PMS export.", variant: "destructive" });
      return;
    }
    const res = parseBulkPaste(rawPaste, {
      defaultCheckoutTime: defaults.checkoutTime,
      defaultCheckinTime: defaults.checkinTime,
      bufferMinutes: defaults.bufferMinutes,
      maxRows,
    });
    const nextRows: BulkRowDraft[] = (res.rows as any[]).map((r: any) => {
      const serviceId = suggestServiceIdFromNotes(r?.notes, services, selectedServiceId);
      return { ...r, serviceId };
    });
    setDraftRows(nextRows);
    track("bulk_parse_done", { rows: res.rows.length, truncated: res.truncated });
    if (res.rows.length > 0) setStep(2);
    if (res.truncated) {
      toast({ title: "Limite righe", description: `Ho importato le prime ${maxRows} righe (limite beta).`, variant: "default" });
    }
  };

  const submit = async () => {
    if (!selectedVendor?.id || !selectedServiceId) return;
    if (draftRows.length === 0) return;
    const invalid = draftRows.filter((r) => (r.errors?.length || 0) > 0);
    if (invalid.length > 0) {
      toast({ title: "Correggi gli errori", description: "Rimuovi o correggi le righe evidenziate prima di inviare.", variant: "destructive" });
      return;
    }

    const normalizedPhone = normalizePhoneForDb(customerPhone || profilePhone);
    if (!normalizedPhone) {
      toast({
        title: "Telefono richiesto",
        description: "Aggiungi un numero di telefono (anche del tuo ufficio) per inviare il bulk order.",
        variant: "destructive",
      });
      setStep(3);
      return;
    }

    setSubmitting(true);
    try {
      track("bulk_submit_start", { rows: draftRows.length });
      const { data: sess } = await supabase.auth.getSession();
      const session = sess?.session;
      if (!session?.user) {
        toast({ title: "Accedi richiesto", description: "Per usare BULK ORDER devi essere autenticato.", variant: "destructive" });
        return;
      }
      const userId = session.user.id;
      const { data: prof } = await supabase.from("profiles").select("id, full_name, email, phone").eq("user_id", userId).maybeSingle();
      const profileId = (prof as any)?.id as string | undefined;
      if (!profileId) {
        toast({ title: "Profilo mancante", description: "Impossibile risolvere il profilo cliente.", variant: "destructive" });
        return;
      }

      const payloadRows = draftRows.map((r) => ({
        service_id: (r as any)?.serviceId || selectedServiceId,
        address: r.address,
        checkout_at: r.checkoutAt?.toISOString(),
        checkin_at: r.checkinAt?.toISOString(),
        guests: r.guests ?? null,
        has_pets: r.hasPets ?? false,
        has_kids: r.hasKids ?? false,
        notes: r.notes ?? null,
        window_start_at: r.windowStartAt?.toISOString(),
        window_end_at: r.windowEndAt?.toISOString(),
        payment_method: paymentMethod,
      }));

      // Invoice: require billing data; create batch immediately (no Stripe)
      if (paymentMethod === "invoice") {
        const billingInfo = billingForm.getValues("billingInfo");
        const err = validateBillingInfo(billingInfo);
        if (err) {
          toast({ title: "Dati fattura obbligatori", description: err, variant: "destructive" });
          setSubmitting(false);
          return;
        }
        const sb: any = supabase;
        const { data, error } = await sb.rpc("create_bulk_public_bookings_by_ids", {
          p_company_id: selectedVendor.id,
          p_service_id: selectedServiceId,
          p_customer_id: profileId,
          p_customer_phone: normalizedPhone,
          p_raw_text: rawPaste,
          p_rows: payloadRows,
          p_invoice_requested: true,
          p_billing_info: billingInfo,
        });
        if (error) throw error;
        const saveBilling = billingForm.getValues("saveBillingToProfile");
        if (saveBilling && profileId && billingInfo) {
          try {
            await sb.from("profiles").update({ billing_info: billingInfo, updated_at: new Date().toISOString() }).eq("id", profileId);
          } catch {
            // ignore
          }
        }
        setCreated({ batch_id: data?.batch_id, booking_ids: data?.booking_ids || [] });
        setSubmittedSnapshot({ vendorName: selectedVendor.company_name, rows: draftRows });
        track("bulk_submit_success", { bookingCount: (data?.booking_ids || []).length });
        toast({ title: "Batch inviato", description: "Pagamento a fattura: il fornitore emetterà la fattura; potrai pagare con bonifico dopo averla ricevuta." });
        setSubmitting(false);
        return;
      }

      // Card: pay total first; bookings are created only after successful payment (via webhook)
      if (paymentMethod === "card") {
        try {
          const token = session?.access_token;
          const resp = await fetch("/api/ops.mjs?action=bulk_checkout", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({
              company_id: selectedVendor.id,
              service_id: selectedServiceId,
              customer_id: profileId,
              customer_phone: normalizedPhone,
              customer_email: (prof as any)?.email ?? session?.user?.email ?? "",
              raw_text: rawPaste,
              rows: payloadRows,
            }),
          });
          const checkoutData = await resp.json().catch(() => ({}));
          if (resp.ok && checkoutData?.url) {
            toast({ title: "Reindirizzamento al pagamento", description: "Pagherai il totale del batch. Le prenotazioni saranno create dopo il pagamento." });
            window.location.assign(checkoutData.url);
            return;
          }
          toast({
            title: "Checkout non disponibile",
            description: checkoutData?.error || "Impossibile avviare il pagamento. Riprova o contatta il supporto.",
            variant: "destructive",
          });
        } catch (err: any) {
          toast({ title: "Errore", description: err?.message || "Checkout non avviato.", variant: "destructive" });
        }
        return;
      }

      // Cash: create batch and bookings immediately; no redirect
      const sb: any = supabase;
      const { data, error } = await sb.rpc("create_bulk_public_bookings_by_ids", {
        p_company_id: selectedVendor.id,
        p_service_id: selectedServiceId,
        p_customer_id: profileId,
        p_customer_phone: normalizedPhone,
        p_raw_text: rawPaste,
        p_rows: payloadRows,
      });
      if (error) throw error;

      setCreated({ batch_id: data?.batch_id, booking_ids: data?.booking_ids || [] });
      setSubmittedSnapshot({ vendorName: selectedVendor.company_name, rows: draftRows });
      track("bulk_submit_success", { bookingCount: (data?.booking_ids || []).length });
      toast({ title: "Batch inviato", description: "Il fornitore potrà programmare gli interventi nella finestra checkout→check-in." });
    } catch (e: any) {
      track("bulk_submit_error", { message: String(e?.message || e) });
      toast({ title: "Errore", description: e?.message || "Invio bulk fallito.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    const payNow = async (bookingId: string) => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const session = sess?.session;
        const token = session?.access_token;
        const resp = await fetch("/api/ops.mjs?action=stripe_checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ booking_id: bookingId }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.url) {
          toast({ title: "Payment error", description: data?.error || "Unable to start checkout", variant: "destructive" });
          return;
        }
        window.location.assign(data.url);
      } catch (e: any) {
        toast({ title: "Payment error", description: e?.message || "Unable to start checkout", variant: "destructive" });
      }
    };

    const isCard = paymentMethod === "card";
    const isInvoice = paymentMethod === "invoice";

    const exportCsv = () => {
      try {
        const rows = (submittedSnapshot?.rows || []).map((r, idx) => ({
          row: idx + 1,
          address: r.address,
          checkout_at: r.checkoutAt ? r.checkoutAt.toISOString() : "",
          checkin_at: r.checkinAt ? r.checkinAt.toISOString() : "",
          window_start_at: r.windowStartAt ? r.windowStartAt.toISOString() : "",
          window_end_at: r.windowEndAt ? r.windowEndAt.toISOString() : "",
          guests: r.guests ?? "",
          has_pets: r.hasPets ? "1" : "0",
          has_kids: r.hasKids ? "1" : "0",
          notes: r.notes ?? "",
        }));
        const header = Object.keys(rows[0] || { row: "" }).join(",");
        const body = rows.map((r) => Object.values(r).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const csv = [header, body].filter(Boolean).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bulk_order_${created.batch_id}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        track("bulk_export_csv", { rows: rows.length });
      } catch {}
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm text-muted-foreground">Batch creato</div>
            <div className="font-medium break-words">ID: {created.batch_id}</div>
            <div className="text-sm text-muted-foreground break-words">{created.booking_ids.length} prenotazioni create</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={exportCsv}>
              Esporta CSV
            </Button>
            {isCard && !isInvoice && created.booking_ids.length > 0 ? (
              <Button
                onClick={() => {
                  // Payment is per-booking; open the first one to start checkout.
                  payNow(created.booking_ids[0]);
                }}
              >
                Paga ora (prima prenotazione)
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => { setCreated(null); setStep(0); setDraftRows([]); setRawPaste(""); setSubmittedSnapshot(null); }}>
              Nuovo bulk order
            </Button>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cosa succede ora</CardTitle>
            <CardDescription>
              Il fornitore vedrà le richieste in inbox e potrà assegnare gli orari entro la finestra.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Puoi ritrovare queste prenotazioni nella tua area profilo.
          </CardContent>
        </Card>
        {submittedSnapshot?.rows?.length ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Righe inviate</CardTitle>
              <CardDescription className="break-words">
                Vendor: {submittedSnapshot.vendorName}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {(submittedSnapshot.rows || []).slice(0, 10).map((r, idx) => (
                <div key={r.id} className="flex items-start justify-between gap-3 border rounded-lg p-2">
                  <div className="min-w-0">
                    <div className="font-medium break-words">{idx + 1}. {r.address}</div>
                    <div className="text-xs text-muted-foreground break-words">
                      {r.checkoutAt ? r.checkoutAt.toLocaleString() : "—"} → {r.checkinAt ? r.checkinAt.toLocaleString() : "—"}
                    </div>
                    <div className="text-xs text-muted-foreground break-words">
                      Servizio: {(services.find((s) => s.id === ((r as any)?.serviceId || selectedServiceId))?.name) || "Servizio"}
                      {" • "}
                      Stima: {(() => {
                        const sid = (r as any)?.serviceId || selectedServiceId;
                        const svc = services.find((s) => s.id === sid);
                        const p = typeof svc?.base_price === "number" ? Number(svc.base_price) : null;
                        return p !== null ? formatCurrency(p) : "—";
                      })()}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">pending</div>
                </div>
              ))}
              {submittedSnapshot.rows.length > 10 && (
                <div className="text-xs text-muted-foreground">
                  Mostrate 10 righe su {submittedSnapshot.rows.length}. Usa “Esporta CSV” per la lista completa.
                </div>
              )}
              {isCard ? (
                <div className="text-xs text-muted-foreground">
                  Se non sei stato reindirizzato al pagamento, paga la prima prenotazione qui sopra oppure da <span className="font-medium">Profilo → Prenotazioni</span> (Paga ora).
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Pagamento contanti: pagherai il fornitore al momento del servizio. Da Profilo puoi usare “Paga ora” per pagare con carta una singola prenotazione.
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground">Wizard</div>
          <div className="font-medium break-words">{stepLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          {selectedVendor?.company_name && (
            <Badge variant="outline" className="max-w-[70vw] truncate">
              Vendor: {selectedVendor.company_name}
            </Badge>
          )}
        </div>
      </div>

      {step === 0 && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Seleziona fornitore e servizio</CardTitle>
            <CardDescription>
              Se conosci già l’azienda, scegli dai preferiti. Altrimenti cerca per città e servizio.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs defaultValue="favorites">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="favorites">Fornitori preferiti</TabsTrigger>
                <TabsTrigger value="search">Cerca fornitore</TabsTrigger>
              </TabsList>

              <TabsContent value="favorites" className="space-y-3">
                {favoriteVendors.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Nessun fornitore preferito ancora. Usa la ricerca.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {favoriteVendors.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVendor(v)}
                        className={`text-left border rounded-xl p-3 hover:bg-gray-50 transition ${
                          selectedVendor?.id === v.id ? "border-primary" : "border-gray-200"
                        }`}
                      >
                        <div className="font-medium break-words">{v.company_name}</div>
                        <div className="text-xs text-muted-foreground">{v.city || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="search" className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <Label>Città / zona</Label>
                    <Input value={searchCity} onChange={(e) => setSearchCity(e.target.value)} placeholder="Es. Jesolo" />
                  </div>
                  <div className="sm:col-span-1">
                    <Label>Servizio</Label>
                    <Select value={searchService} onValueChange={setSearchService}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_PRESETS.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-1 flex items-end">
                    <Button className="w-full" onClick={doSearchVendors} disabled={searchLoading}>
                      {searchLoading ? "Ricerca…" : "Cerca fornitori"}
                    </Button>
                  </div>
                </div>

                {searchResults.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {searchResults.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVendor(v)}
                        className={`text-left border rounded-xl p-3 hover:bg-gray-50 transition ${
                          selectedVendor?.id === v.id ? "border-primary" : "border-gray-200"
                        }`}
                      >
                        <div className="font-medium break-words">{v.company_name}</div>
                        <div className="text-xs text-muted-foreground">{v.city || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-medium">Servizio specifico</div>
                <div className="text-xs text-muted-foreground">
                  Seleziona il servizio del vendor su cui creare le prenotazioni.
                </div>
              </div>
              <Select value={selectedServiceId} onValueChange={setSelectedServiceId} disabled={!selectedVendor?.id || services.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={!selectedVendor?.id ? "Seleziona prima un vendor" : "Seleziona un servizio"} />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedVendor?.id ? null : services.length === 0 ? (
                <div className="text-xs text-muted-foreground">Nessun servizio pubblico disponibile per questo vendor.</div>
              ) : null}
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(1)} disabled={!canContinueFromStep0}>
                Continua
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Incolla lista</CardTitle>
            <CardDescription>
              Incolla fino a <span className="font-medium">30</span> righe con indirizzo, checkout e check-in. Puoi incollare anche da email o WhatsApp.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-3 text-xs text-blue-900">
              <div className="font-medium mb-1">Cos'è il checkout e il check-in qui?</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li><strong>Checkout</strong> = data e ora in cui gli <strong>ospiti attualmente in casa</strong> escono.</li>
                <li><strong>Check-in</strong> = data e ora in cui i <strong>prossimi ospiti</strong> arrivano.</li>
                <li>Il fornitore programmerà la <strong>pulizia</strong> in mezzo (tra checkout e check-in, meno il buffer).</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Ora checkout default</Label>
                <Input value={defaults.checkoutTime} onChange={(e) => setDefaults((d) => ({ ...d, checkoutTime: e.target.value }))} placeholder="11:00" />
                <div className="text-[11px] text-muted-foreground">Uscita ospiti attuali</div>
              </div>
              <div className="space-y-1">
                <Label>Ora check-in default</Label>
                <Input value={defaults.checkinTime} onChange={(e) => setDefaults((d) => ({ ...d, checkinTime: e.target.value }))} placeholder="15:00" />
                <div className="text-[11px] text-muted-foreground">Arrivo prossimi ospiti</div>
              </div>
              <div className="space-y-1">
                <Label>Buffer (min)</Label>
                <Input
                  type="number"
                  value={defaults.bufferMinutes}
                  onChange={(e) => setDefaults((d) => ({ ...d, bufferMinutes: Math.max(0, Number(e.target.value || 0)) }))}
                />
                <div className="text-[11px] text-muted-foreground">Sicurezza prima del check-in</div>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Lista (incolla qui)</Label>
              <textarea
                value={rawPaste}
                onChange={(e) => setRawPaste(e.target.value)}
                className="w-full min-h-[220px] rounded-xl border border-gray-200 p-3 text-sm"
                placeholder={`Formato per riga: indirizzo | uscita ospiti attuali | arrivo prossimi ospiti | ospiti/note\n\nEsempi:\nVia Roma 10, Jesolo\t10/02/2026\t10/02/2026\t4 pax\nVia Milano 5, Jesolo\t11/02/2026 11:00\t11/02/2026 15:00\t2 pax + cane`}
              />
              <div className="text-xs text-muted-foreground">
                La finestra di pulizia è calcolata tra <strong>checkout</strong> (uscita) e <strong>check-in</strong> (arrivo), meno il buffer.
              </div>
            </div>

            <div className="flex justify-between gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setStep(0)}>
                Indietro
              </Button>
              <Button onClick={parseNow}>Analizza e continua</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Controlla e correggi</CardTitle>
            <CardDescription>
              {parsedSummary.total} righe • {parsedSummary.valid} pronte • {parsedSummary.invalid} con errori
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <BulkImportTable rows={draftRows} onChange={setDraftRows} bufferMinutes={defaults.bufferMinutes} services={services.map(s => ({ id: s.id, name: s.name }))} />
            <div className="flex justify-between gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setStep(1)}>
                Indietro
              </Button>
              <Button onClick={() => setStep(3)} disabled={parsedSummary.total === 0}>
                Riepilogo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Riepilogo</CardTitle>
            <CardDescription>
              Verifica prima di inviare al fornitore. In beta: max 30 righe per batch.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="border rounded-xl p-3">
                <div className="text-muted-foreground">Vendor</div>
                <div className="font-medium break-words">{selectedVendor?.company_name || "—"}</div>
              </div>
              <div className="border rounded-xl p-3">
                <div className="text-muted-foreground">Righe</div>
                <div className="font-medium">{draftRows.length}</div>
              </div>
              <div className="border rounded-xl p-3">
                <div className="text-muted-foreground">Errori</div>
                <div className="font-medium">{parsedSummary.invalid}</div>
              </div>
            </div>

            <div className="border rounded-xl p-3 text-sm">
              <div className="text-muted-foreground mb-2">Righe per servizio</div>
              <div className="space-y-1">
                {Object.entries(serviceCounts).map(([sid, count]) => (
                  <div key={sid} className="flex items-center justify-between gap-3">
                    <div className="min-w-0 truncate">
                      {(services.find((s) => s.id === sid)?.name) || "Servizio"}
                    </div>
                    <div className="font-medium shrink-0">{count}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border rounded-xl p-3 text-sm">
              <div className="text-muted-foreground mb-2">Riepilogo prezzi (stima)</div>
              <div className="space-y-1">
                {pricingSummary.byService.map((row) => (
                  <div key={row.serviceId} className="flex items-center justify-between gap-3">
                    <div className="min-w-0 truncate">
                      {row.count}× {row.serviceName}
                      {row.unit !== null ? (
                        <span className="text-muted-foreground"> • {formatCurrency(row.unit)}</span>
                      ) : (
                        <span className="text-muted-foreground"> • prezzo da definire</span>
                      )}
                    </div>
                    <div className="font-medium shrink-0">
                      {row.subtotal !== null ? formatCurrency(row.subtotal) : "—"}
                    </div>
                  </div>
                ))}
              </div>
              <Separator className="my-3" />
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium">Totale servizi (prezzo base)</div>
                <div className="text-lg font-bold">
                  {pricingSummary.hasUnknown ? `${formatCurrency(pricingSummary.total)}+` : formatCurrency(pricingSummary.total)}
                </div>
              </div>
              {customerFeeEstimate.cents > 0 ? (
                <div className="flex items-center justify-between gap-3 text-sm mt-2">
                  <div className="text-muted-foreground">
                    Commissione piattaforma (stima, a carico cliente)
                  </div>
                  <div className="font-medium">{formatCurrency(customerFeeEstimate.euros)}</div>
                </div>
              ) : null}
              {customerFeeEstimate.anyFeeDisclaimer ? (
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  La stima della commissione segue le regole applicate alle prenotazioni (max 2% per intervento, tetto 10€).
                  Il pagamento con carta addebita prima il totale dei servizi a prezzo base; le commissioni restano tracciate
                  a livello di prenotazione come da termini del marketplace.
                </p>
              ) : (
                <div className="text-xs text-muted-foreground mt-2">
                  La stima usa il prezzo base del servizio. Extra o varianti possono essere concordati col fornitore.
                </div>
              )}
            </div>

            <div className="border rounded-xl p-3 text-sm">
              <div className="text-muted-foreground mb-2">Metodo di pagamento</div>
              <Select value={paymentMethod} onValueChange={(v) => { const next = (v as "card" | "cash" | "invoice") || "card"; setPaymentMethod(next); if (next === "invoice") setRequestInvoice(true); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">Carta (paga online)</SelectItem>
                  <SelectItem value="cash">Contanti (paghi al fornitore)</SelectItem>
                  <SelectItem value="invoice">Solo Fattura (bonifico dopo fattura)</SelectItem>
                </SelectContent>
              </Select>
              {paymentMethod !== "invoice" && (
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input type="checkbox" checked={requestInvoice} onChange={(e) => setRequestInvoice(e.target.checked)} />
                  <span>Richiedi fattura (dati per emissione obbligatori)</span>
                </label>
              )}
              {(paymentMethod === "invoice" || requestInvoice) && (
                <div className="mt-3 p-3 border rounded-lg bg-muted/30">
                  <div className="text-sm font-medium mb-2">Dati per la fattura</div>
                  <FormProvider {...billingForm}>
                    <InvoiceBillingForm showSaveToProfile={true} />
                  </FormProvider>
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-2">
                Se scegli carta potrai pagare subito dopo l’invio (anche singolarmente per prenotazione).
              </div>
            </div>

            <div className="space-y-1">
              <Label>Telefono di contatto (obbligatorio)</Label>
              <Input
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="Es. +39 333 123 4567"
              />
              <div className="text-xs text-muted-foreground">
                Useremo questo numero come contatto cliente sulla prenotazione. Se nel tuo profilo è vuoto, inseriscilo qui.
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Il fornitore programmerà la pulizia tra l'uscita degli ospiti attuali (<strong>checkout</strong>) e l'arrivo dei prossimi (<strong>check-in</strong>). Se una riga non ha una finestra valida, verrà rifiutata.
            </div>

            <div className="flex justify-between gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setStep(2)}>
                Indietro
              </Button>
              <Button onClick={submit} disabled={submitting || parsedSummary.total === 0 || parsedSummary.invalid > 0}>
                {submitting
                  ? paymentMethod === "card"
                    ? "Invio e pagamento…"
                    : "Invio…"
                  : paymentMethod === "card"
                    ? "Paga e invia"
                    : paymentMethod === "invoice"
                      ? "Invia a fattura"
                      : "Invia bulk order"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

